/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Loader } from 'cdk-radial';
import { isEqual } from 'lodash'
import Page from '../../../page/index';
import SolutionHeader from '../../SolutionHeader/SolutionHeader';
import DMSAccountsList from './DMSAccountsList/DMSAccountsList';
import { setScreen } from '../../../../redux/connectedComponent/uiFactory/uiFactory.slice';
import { screens } from '../../../config/uiFactory.config';
import './EditDMSAccounts.scss';
import {
    getDMSAccountDetailsFromEISForEditDMSAccounts,
    mergedDMSAccountsList,
    updateDMSAccountsDetailsForApp,
    clearUpdateDMSAccounts
} from '../../../../redux/connectedComponent/updateDMSAccounts/updateDMSAccounts.slice';
import Notification from '../../../notification/Notification';
import LearnMore from '../../../LearnMore/LearnMore';
import { sendAmplitudeData } from '../../../../utils/amplitude';
import { AMPLITUDE_EVENTS,AMPLITUDE_PROPERTIES } from '../../../../constants/amplitudeConstants';
import { trackPageView } from '../../../../utils/gtm';

const DMS_ACCOUNTS = 'Manage DMS Accounts';
const DMS_ACCOUNTS_DESCRIPTION = 'Select the DMS accounts to use with this app.';
const FETCHING_DMS_ACCOUTS_ERROR = 'Error in Fetching DMS Accounts Details, please try again';
const POSITIVE = 'positive';
const NEGATIVE = 'negative';

const mergeDMSAccountsData = (dmsAccountListForApp, getEISDMSInfo) => dmsAccountListForApp.map((dmsAccountData, i) => {
    const {
        api: apiVal,
        cmfNumber: cmfNumberVal,
        dmsAccountType: dmsAccountTypeVal,
        dmsAccount: dmsAccountVal,
        departments: departmentsVal,
        entityId: entityIdVal,
        id: idVal,
        location: locationVal,
        type: typeVal
    } = dmsAccountData;
    const eisDMSAccountsData = getEISDMSInfo.find(({
        api, cmfNumber, dmsAccountType, entityId, id, location, type
    }) => isEqual(apiVal, api)
            && cmfNumberVal === cmfNumber
            && dmsAccountTypeVal === dmsAccountType
            && entityId === entityIdVal
            && idVal === id
            && locationVal === location
            && typeVal === type
    );

    const dmsAccountsArray = getEISDMSInfo
        .map(item => ({
            entityId: item.entityId,
            location: item.location,
            dmsAccount: item.dmsAccount,
            departments: item.departments
        }))
        // take only the first entry for each entityId + location -
        // each store has its own departments
        .filter((v, i, a) => a.findIndex(t => (t.entityId === v.entityId && t.location === v.location)) === i);

    // if we're working on an old dms entry without departments, then guess
    // the department based on dmsAccount
    let existingDepts = departmentsVal ||
        eisDMSAccountsData?.departments?.filter(d => d.name === dmsAccountVal) ||
        [];
    if (existingDepts.length) {
        // if we pulled a department from a legacy entry, propagate the change back
        // to dmsAccountListForApp so the department gets added when the user saves
        dmsAccountListForApp[i] = {
            ...dmsAccountData,
            departments: existingDepts
        };
    }

    const otherEisDepts = eisDMSAccountsData?.departments
        ?.filter(item => !existingDepts.some(d => d.id === item.id));

    const entry = dmsAccountsArray.find(ele => ele.entityId === entityIdVal && ele.location === locationVal);
    let dms = [];
    let otherDepts = [];
    if (entry) {
        dms = entry.dmsAccount.filter(item => item !== dmsAccountVal);
        otherDepts = entry.departments.filter(item => !existingDepts.some(d => d.id === item.id));
    }
    return (
        eisDMSAccountsData && {
            ...dmsAccountData,
            dmsAccount: [...new Set([dmsAccountVal, ...eisDMSAccountsData.dmsAccount])],
            departments: [...existingDepts, ...otherEisDepts]
        }
    ) || {
        ...dmsAccountData,
        dmsAccount: [dmsAccountData.dmsAccount, ...dms],
        departments: [...existingDepts, ...otherDepts]
    };
});

const EditDMSAccounts = ({ solutionData, additionalData }) => {
    const {
        id: solutionId,
        overview: {
            name: solutionName,
            publisherName
        }
    } = solutionData;
    const dispatch = useDispatch();
    const state = useSelector(state => state);

    const authToken = state.loginDetailsSlice && state.loginDetailsSlice.authenticationToken;
    const userEmail = state.loginDetailsSlice && state.loginDetailsSlice.userEmail;
    const {
        getEISDMSInfo,
        isEISDMSInfoLoading,
        isEISDMSInfoSuccess,
        updatedDMSAcounts,
        errorInGetEISDMSInfo,
        isDMSAccountsUpdateSuccess,
        isDMSAccountsUpdateLoading,
        errorInDMSAccountsUpdate
    } = state.updateDMSAccountsSlice;

    const { subscriptionData } = additionalData;

    const [saveButton, setSaveButton] = useState(true);
    const [showNotification, setShowNotification] = useState(false);
    const [notificationContent, setNotificationContent] = useState(false);
    const [notificationType, setNotificationType] = useState(false);

    const dmsAccountListForApp = subscriptionData && subscriptionData.reduce((acc, curr) => {
        let entities = curr.entityIdList ? curr.entityIdList : [];
        acc = [...acc, ...entities];
        return acc;
    }, []);

    const entitiesWithDMSAccounts = dmsAccountListForApp.filter(
        entity => entity.hasOwnProperty('dmsAccount') &&
            entity.hasOwnProperty('dmsAccountType') &&
            entity.hasOwnProperty('id'));

    const dmsInfoDetails = entitiesWithDMSAccounts && entitiesWithDMSAccounts.reduce((acc, curr) => {
        if(!acc.dmsConfigInfo || (acc.dmsConfigInfo && !acc.dmsConfigInfo.includes(curr.type))) {
            acc.dmsConfigInfo = [...acc.dmsConfigInfo, curr.type];
        }
        if (curr.type) {
            const apis = Array.isArray(curr.api) ? curr.api : [curr.api];
            for (const api of apis) {
                const existingDmsTypes = acc.dmsApiDetails[api]?.[0] || [];
                const newDmsTypes = !existingDmsTypes.includes(curr.type)
                  ? [...existingDmsTypes, curr.type]
                  : existingDmsTypes;
                acc.dmsApiDetails = {
                    ...acc.dmsApiDetails,
                    [api]: [newDmsTypes]
                }
            }
        }
        return acc;
    }, { dmsConfigInfo: [], dmsApiDetails: {} });

    useEffect(() => {
        trackPageView('Manage DMS Accounts');
        clearUpdateDMSAccounts();
    }, []);

    useEffect(() => {
        Promise.all([
            subscriptionData.map(locationData =>
              dispatch(
                getDMSAccountDetailsFromEISForEditDMSAccounts({
                  location: locationData.entityIdList && locationData.entityIdList.length && locationData.entityIdList[0].location,
                  cmfNumber: locationData.entitycmfNumber,
                  storeId: locationData.storeId,
                  entityId: locationData.entityId,
                  token: authToken,
                  dmsInfoDetails
                })
              )
            )
          ]);
    }, [subscriptionData]);

    useEffect(() => {
        const mergedDMSAccountsData = mergeDMSAccountsData(entitiesWithDMSAccounts, getEISDMSInfo);
        const dmsAccountsDataByDMSTypes = mergedDMSAccountsData.reduce((acc, curr) => {
            if (curr.type && (!acc || !acc[curr.type])) {
                acc = { ...acc, [curr.type]: [curr] };
            }
            if (Object.keys(acc).includes(curr.type)) {
                acc[curr.type] = [...new Set([...acc[curr.type], curr])];
            }
            return acc;
        }, {});
        dispatch(mergedDMSAccountsList({ dmsAccountsDataByDMSTypes, dmsAccountListForApp: entitiesWithDMSAccounts }));
    }, [getEISDMSInfo, getDMSAccountDetailsFromEISForEditDMSAccounts]);

    useEffect(() => {
        let accountsWithBlankDMS = [];
        if (updatedDMSAcounts && updatedDMSAcounts.length) {
            accountsWithBlankDMS = updatedDMSAcounts.filter(account => {
                return !account.dmsAccount;
            });
        }
        setSaveButton(!!accountsWithBlankDMS.length);
    }, [updatedDMSAcounts]);

    useEffect(() => {
        if(isDMSAccountsUpdateSuccess) {
            setNotificationContent(
                `You updated DMS Accounts for app ${solutionName} successfully`
            );
            setNotificationType(POSITIVE);
            setShowNotification(true);
        } else if(errorInDMSAccountsUpdate) {
            setNotificationContent(`Failed to update DMS Accounts for app ${solutionName}, please try again`);
            setNotificationType(NEGATIVE);
            setShowNotification(true);
        } else {
            setShowNotification(false);
        }
    }, [isDMSAccountsUpdateSuccess, errorInDMSAccountsUpdate]);

    const onBackButtonClickHandler = () => {
        dispatch(clearUpdateDMSAccounts());
        dispatch(setScreen({screen: screens.SUBSCRIPTION_MANAGEMENT}));
    };

    const onSaveDMSAccountsHandler = () => {
        sendAmplitudeData(AMPLITUDE_EVENTS.MANAGE_DMS_ACCOUNTS,{
            [AMPLITUDE_PROPERTIES.APP_NAME] : solutionData?.overview?.name || solutionData?.displayName,
            [AMPLITUDE_PROPERTIES.APP_ID]: solutionData?.id
        })
        dispatch(updateDMSAccountsDetailsForApp({
            token: authToken,
            solutionId,
            updatedDMSAcounts,
            isNewAPI: false
        }));
    }

    return (
        <Page>
            <SolutionHeader
                solutionData={solutionData}
                onBackButtonClickHandler={onBackButtonClickHandler}
            />
            <p className="dms-header-title-display">{DMS_ACCOUNTS}</p>
            <p className="dms-header-subtitle-display">
                {DMS_ACCOUNTS_DESCRIPTION}{' '}
                <LearnMore
                  docPath="/docs/marketplace/marketplace-for-apps/managing-app-configurations/#managing-dms-accounts"
                  screen="DMS Configuration"
                />
            </p>
            {isEISDMSInfoLoading && (
                <div className="dms-loader">
                    <Loader label="Fetching DMS Accounts Details..." />
                </div>
            )}

            {!isEISDMSInfoLoading && !isEISDMSInfoSuccess && errorInGetEISDMSInfo && (
                <p className="dms-accounts-error">{FETCHING_DMS_ACCOUTS_ERROR}</p>
            )}

            {!isEISDMSInfoLoading && !isDMSAccountsUpdateLoading && isEISDMSInfoSuccess && (
              <DMSAccountsList solutionData={solutionData} disableSave={() => setSaveButton(true)} />
            )}
            {!isEISDMSInfoLoading && !isDMSAccountsUpdateLoading && (
                <div className="dms-next-button">
                    <Button
                        backgroundAppearance="light"
                        iconColor="primary"
                        iconSize={24}
                        isDisabled={saveButton}
                        onClick={onSaveDMSAccountsHandler}
                        size="standard"
                        text="Save"
                    />
                </div>
            )}
            {isDMSAccountsUpdateLoading && (
                <div className="dms-loader">
                    <Loader label="Updating DMS Accounts Details..." />
                </div>
            )}
            {
                showNotification &&
                <Notification
                    content={notificationContent}
                    variant={notificationType}
                    actions={[{"text":"Dismiss"}]}
                    onDismiss={() => setShowNotification(false)}
                    customStyle={{ marginTop: '70px' }}
                />
            }
        </Page>
    );
}

export default EditDMSAccounts;
